import React, { useState } from 'react'
import { Box, Button, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import { without } from '@/common/collection-utils.ts'
import { useCollectionViewContext } from './CollectionViewPageContext'
import { useDiRef } from '@/common/react/di-hooks'
import type { CollectionViewsService } from '@/objects/services/CollectionViewsService'
import { useBehaviorObservable, useObservable } from '@/common/react/observable-hooks'
import { FilterLogicalOperator, FilterOperator } from '@/generated/graphql'
import type { PropertyDefinitionModel } from '@/core/gql/gql-models-override.ts'
import { createTmpClientId } from '@/common/network/gql-types.ts'
import type { PropertyValue } from '@/objects/gql/PropertyValue.ts'
import {
  accentBlackWhiteButtonStyles,
  coloredSubmitButtonSx,
  deleteButtonSx,
} from '@/core/theme/style-variants.ts'
import type { Theme } from '@mui/material/styles'
import { isSupportedType, operatorsFor } from '@/objects/services/ObjectFilterService.ts'
import { MultiSelectPropertyInput } from '@/objects/property-forms/multi-select/MultiSelectPropertyInput.tsx'
import {
  type FilterNodeModel,
  type GroupFilterNodeModel,
  isGroupFilterNode,
  makeFilterGroup,
  makeFilterLeaf,
  type OperatorFilterNodeModel,
  toFilterNodeInput,
} from '@/core/gql/filter-models.ts'
import { assertLog, log, logJson } from '@/common/errors/log-utils.ts'

const compactSelectSx = {
  height: '32px',
  '& .MuiSelect-select': { py: '2px' },
}

const groupSx = (theme: Theme) => ({
  background: theme.palette.paperElevation1,
  border: `1px solid ${theme.palette.divider}`,
  borderColor: 'divider',
  borderRadius: 1,
  p: 1,
})

export function FilterPopover() {
  const { collectionId, objectType } = useCollectionViewContext()
  const collectionViewsService = useDiRef<CollectionViewsService>('CollectionViewsService')

  const activeTab = useObservable(collectionViewsService.getActiveTab$(collectionId), () =>
    collectionViewsService.getActiveTab(collectionId),
  )

  const [nodes, setNodes] = useState<FilterNodeModel[]>(() => {
    const existing = activeTab?.filterTree ?? []
    log('filter popover nodes init', { state: existing, pretty: true })
    if (existing.length === 0) return [makeFilterLeaf(null)]
    return expandOnLoad(existing)
  })
  const supportedPropDefs = (objectType?.propertyDefinitions ?? []).filter((p) =>
    isSupportedType(p.type),
  )

  const singleLeaf = nodes.length === 1 ? (nodes[0] as OperatorFilterNodeModel) : null
  const groups = nodes.filter(isGroupFilterNode)
  const rootGroup = groups.find((n) => n.parentId == null) ?? null
  const lvl1Groups = rootGroup ? groups.filter((n) => n.parentId === rootGroup.id) : []

  function update(updated: FilterNodeModel) {
    setNodes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)))
  }

  function deleteNode(id: number) {
    setNodes((prev) => {
      const deletedNode = prev.find((n) => n.id === id)
      let result = prev.filter((n) => n.id !== id && n.parentId !== id)

      if (!isGroupFilterNode(deletedNode!)) {
        const parentId = deletedNode!.parentId
        if (!result.some((n) => n.parentId === parentId)) {
          result = result.filter((n) => n.id !== parentId)
        }
      }

      const root = result.find((n) => n.parentId == null && isGroupFilterNode(n))
      if (root && !result.some((n) => n.parentId === root.id)) {
        result = without(result, root)
      }

      return result
    })
  }

  function addFilter() {
    if (singleLeaf) {
      // convert: wrap existing leaf + new leaf each in their own lvl-1 group under a new root group
      const root = makeFilterGroup(null)
      const grp1 = makeFilterGroup(root.id)
      const grp2 = makeFilterGroup(root.id)
      const newLeaf = makeFilterLeaf(grp2.id)
      setNodes([root, grp1, { ...singleLeaf, parentId: grp1.id }, grp2, newLeaf])
    } else if (rootGroup) {
      // add a new lvl-1 group with one leaf to root group
      const grp = makeFilterGroup(rootGroup.id)
      const leaf = makeFilterLeaf(grp.id)
      setNodes((prev) => [...prev, grp, leaf])
    } else {
      setNodes([makeFilterLeaf(null)])
    }
  }

  function addLeafToGroup(groupId: number) {
    setNodes((prev) => [...prev, makeFilterLeaf(groupId)])
  }

  function onSubmit() {
    collectionViewsService.updateActiveTab(collectionId, {
      filterTree: flattenForSave(nodes).map(toFilterNodeInput),
    })

    log('submit', { state: { tree: nodes.map(toFilterNodeInput) }, pretty: true })
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 650 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2">Filters</Typography>
        {nodes.length > 0 && (
          <Button size="small" sx={deleteButtonSx} onClick={() => setNodes([])}>
            Clear all
          </Button>
        )}
      </Box>

      {singleLeaf && (
        <Box sx={groupSx}>
          <FilterRow
            node={singleLeaf}
            propDefs={supportedPropDefs}
            onUpdate={update}
            onDelete={() => deleteNode(singleLeaf.id)}
            autoOpenPropSelect
          />
        </Box>
      )}

      {rootGroup && (
        <Box
          sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, alignItems: 'center' }}
        >
          <LogicalOperatorSelect
            value={rootGroup.logicalOperator ?? FilterLogicalOperator.And}
            onChange={(op) => update({ ...rootGroup, logicalOperator: op })}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {lvl1Groups.map((grp) => (
              <GroupBox
                key={grp.id}
                group={grp}
                leaves={nodes.filter((n): n is OperatorFilterNodeModel => n.parentId === grp.id)}
                propDefs={supportedPropDefs}
                onUpdateGroup={update}
                onDeleteGroup={() => deleteNode(grp.id)}
                onAddLeaf={() => addLeafToGroup(grp.id)}
                onUpdateLeaf={update}
                onDeleteLeaf={deleteNode}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          size="small"
          startIcon={
            <AddIcon
              sx={(theme) => ({
                color: theme.palette.grey[600],
              })}
            />
          }
          onClick={addFilter}
          sx={[{ alignSelf: 'flex-start' }, nodes.length === 0 ? accentBlackWhiteButtonStyles : {}]}
        >
          <Typography
            variant="body1"
            textTransform="none"
            sx={(theme) => ({
              color: theme.palette.grey[600],
            })}
          >
            New filter
          </Typography>
        </Button>

        <Button
          size="small"
          type="submit"
          variant="contained"
          onClick={() => {
            void onSubmit()
          }}
          sx={coloredSubmitButtonSx}
        >
          Save
        </Button>
      </Box>
    </Box>
  )
}

function FilterRow({
  node,
  propDefs,
  onUpdate,
  onDelete,
  autoOpenPropSelect,
}: {
  node: OperatorFilterNodeModel
  propDefs: PropertyDefinitionModel[]
  onUpdate: (updated: FilterNodeModel) => void
  onDelete: () => void
  autoOpenPropSelect?: boolean
}) {
  const propDef = propDefs.find((p) => p.id === node.propertyDefinitionId)
  const propType = propDef?.type

  function setProp(propDefId: number) {
    const newProp = propDefs.find((p) => p.id === propDefId)!
    onUpdate({
      ...node,
      propertyDefinitionId: propDefId,
      operator: operatorsFor(newProp.type)[0].value,
      value: null,
      values: null,
    })
  }

  function setOperator(op: FilterOperator) {
    onUpdate({ ...node, operator: op, value: null, values: null })
  }

  function setValue(val: PropertyValue) {
    onUpdate({ ...node, value: val, values: null })
  }

  function setValues(vals: number[]) {
    onUpdate({ ...node, values: vals, value: null })
  }

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 1,
        color: theme.palette.paperElevation1,
      })}
    >
      {/* Property select */}
      <Select
        size="small"
        value={node.propertyDefinitionId ?? ''}
        onChange={(e) => setProp(Number(e.target.value))}
        defaultOpen={autoOpenPropSelect}
        displayEmpty
        sx={{ minWidth: 130, ...compactSelectSx }}
      >
        <MenuItem value="" disabled>
          <em>Property</em>
        </MenuItem>
        {propDefs.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.name}
          </MenuItem>
        ))}
      </Select>

      {/* Operator select */}
      {propType && (
        <Select
          size="small"
          value={node.operator ?? ''}
          onChange={(e) => setOperator(e.target.value as FilterOperator)}
          sx={{ minWidth: 120, ...compactSelectSx }}
        >
          {operatorsFor(propType).map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      )}

      {/* Value input */}
      {propType === 'text' && (
        <TextField
          size="small"
          value={String(node.value ?? '')}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Value"
          sx={{ minWidth: 140 }}
        />
      )}
      {propType === 'select-1' && propDef?.options && (
        <Select
          size="small"
          value={String(node.value ?? '')}
          onChange={(e) => setValue(e.target.value)}
          displayEmpty
          sx={{ minWidth: 140, ...compactSelectSx }}
        >
          <MenuItem value="" disabled>
            <em>Select</em>
          </MenuItem>
          {propDef.options.map((opt) => (
            <MenuItem key={opt.id} value={String(opt.id)}>
              {opt.value}
            </MenuItem>
          ))}
        </Select>
      )}
      {propType === 'multi-select' && propDef?.options && (
        <MultiSelectPropertyInput
          options={propDef.options}
          value={node.values ?? []}
          onValueChange={setValues}
        />
      )}

      <IconButton size="small" onClick={onDelete}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

function GroupBox({
  group,
  leaves,
  propDefs,
  onUpdateGroup,
  onDeleteGroup,
  onAddLeaf,
  onUpdateLeaf,
  onDeleteLeaf,
}: {
  group: GroupFilterNodeModel
  leaves: OperatorFilterNodeModel[]
  propDefs: PropertyDefinitionModel[]
  onUpdateGroup: (updated: FilterNodeModel) => void
  onDeleteGroup: () => void
  onAddLeaf: () => void
  onUpdateLeaf: (updated: FilterNodeModel) => void
  onDeleteLeaf: (id: number) => void
}) {
  const hasMultipleFilters = leaves.length > 1

  return (
    <Box
      sx={(theme) => ({
        ...groupSx(theme),
        display: hasMultipleFilters ? 'grid' : 'flex',
        ...(hasMultipleFilters
          ? { gridTemplateColumns: 'auto 1fr', gap: 1, alignItems: 'center' }
          : { flexDirection: 'column', gap: 1 }),
        '&:hover .group-delete-btn': { visibility: 'visible' },
      })}
    >
      {hasMultipleFilters && (
        <LogicalOperatorSelect
          value={group.logicalOperator}
          onChange={(op) => onUpdateGroup({ ...group, logicalOperator: op })}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          ...(hasMultipleFilters && { gridColumn: '2 / 3' }),
        }}
      >
        {leaves.map((leaf) => (
          <FilterRow
            key={leaf.id}
            node={leaf}
            propDefs={propDefs}
            onUpdate={onUpdateLeaf}
            onDelete={() => onDeleteLeaf(leaf.id)}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          ...(hasMultipleFilters && { gridColumn: '1 / -1' }),
        }}
      >
        <Button
          size="small"
          startIcon={<AddIcon sx={(theme) => ({ color: theme.palette.grey[600] })} />}
          onClick={onAddLeaf}
          sx={(theme) => ({
            background: 'none',
            border: 'none',
            boxShadow: 'none',
            color: theme.palette.grey[600],
            py: '2px',
            px: '8px',
            '& .MuiButton-startIcon': { marginRight: '2px' },
            '&:hover': {
              background: theme.palette.action.hover,
            },
          })}
        >
          <Typography
            variant="body2"
            textTransform="none"
            sx={(theme) => ({ color: theme.palette.grey[600] })}
          >
            Filter in group
          </Typography>
        </Button>
        {hasMultipleFilters && (
          <Button
            className="group-delete-btn"
            size="small"
            onClick={onDeleteGroup}
            sx={[deleteButtonSx, { visibility: 'hidden' }]}
          >
            <Typography variant="body2" textTransform="none">
              Clear group
            </Typography>
          </Button>
        )}
      </Box>
    </Box>
  )
}

function LogicalOperatorSelect({
  value,
  onChange,
}: {
  value: FilterLogicalOperator
  onChange: (op: FilterLogicalOperator) => void
}) {
  return (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value as FilterLogicalOperator)}
      sx={{ minWidth: 80 }}
    >
      <MenuItem value={FilterLogicalOperator.And}>AND</MenuItem>
      <MenuItem value={FilterLogicalOperator.Or}>OR</MenuItem>
    </Select>
  )
}

function findRootGroupNode(nodes: FilterNodeModel[]): GroupFilterNodeModel | undefined {
  return nodes.find((n) => isGroupFilterNode(n) && n.parentId == null) as
    | GroupFilterNodeModel
    | undefined
}

/** Load: direct leaf children of root → each wrapped in its own lvl-1 group */
function expandOnLoad(nodes: FilterNodeModel[]): FilterNodeModel[] {
  const rootGroup = findRootGroupNode(nodes)
  if (!rootGroup) {
    assertLog(nodes.length === 1, 'expandOnLoad: no root group but not a single leaf', { nodes })
    return nodes
  }

  const directLeaves = nodes.filter((n) => !isGroupFilterNode(n) && n.parentId === rootGroup.id)
  if (directLeaves.length === 0) return nodes

  const result = nodes.filter((n) => !directLeaves.includes(n))
  for (const leaf of directLeaves) {
    const grp = makeFilterGroup(rootGroup.id)
    result.push(grp, { ...leaf, parentId: grp.id })
  }
  return result
}

/** Save: lvl-1 groups with exactly 1 leaf → unwrap leaf to direct root child, drop group */
function flattenForSave(nodes: FilterNodeModel[]): FilterNodeModel[] {
  const rootGroup = findRootGroupNode(nodes)
  if (!rootGroup) {
    assertLog(nodes.length === 1, 'flattenForSave: no root group but not a single leaf', { nodes })
    return nodes
  }

  const singleLeafGroupIds = new Set(
    nodes
      .filter((n) => isGroupFilterNode(n) && n.parentId === rootGroup.id && nodes.filter((c) => c.parentId === n.id).length === 1)
      .map((grp) => grp.id),
  )
  if (singleLeafGroupIds.size === 0) return nodes

  const flattened = nodes.flatMap((n) => {
    if (isGroupFilterNode(n) && singleLeafGroupIds.has(n.id)) return []
    if (!isGroupFilterNode(n) && n.parentId != null && singleLeafGroupIds.has(n.parentId))
      return [{ ...n, parentId: rootGroup.id }]
    return [n]
  })

  const rootChildren = flattened.filter((n) => n.parentId === rootGroup.id)
  if (rootChildren.length === 1 && !isGroupFilterNode(rootChildren[0])) {
    return [{ ...rootChildren[0], parentId: null }]
  }
  return flattened
}
